import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { InView } from 'react-intersection-observer';
import { useQuery } from 'react-query';

import CustomSpinner, { CustomSpinnerColor } from '@components/Spinner/CustomSpinner';
import PollCard from '@components/pages/Forum/PollCard';
import PollResult from '@components/pages/Forum/PollResult';
import { useCurrentUser, useEventTracking } from '@hooks';
import { ClickTarget, ImpressionElement, TrackingLocation, click, impression } from '@tracking';
import { Poll, castStandalonePollVote, fetchPoll, isClosedPoll } from '@utils/backend-api/polls';

const ARTICLE_POLL_QUERY_KEY = 'article_poll';
const ARTICLE_POLL_STALE_TIME = 5 * 60 * 1000;

export default function ArticlePoll({ pollId }: { pollId: string }) {
  const { currentUser } = useCurrentUser();
  const { Track, trackEvent } = useEventTracking({
    location: TrackingLocation.ContentBody,
  });

  const [localPoll, setLocalPoll] = useState<Poll | null>(null);

  const { data: fetchedPoll, isLoading } = useQuery(
    [ARTICLE_POLL_QUERY_KEY, pollId],
    () => fetchPoll(pollId),
    { staleTime: ARTICLE_POLL_STALE_TIME }
  );

  const poll = localPoll || fetchedPoll;

  const handleVote = async (optionId: number) => {
    if (!poll) return;

    try {
      const updated = await castStandalonePollVote({ pollId, pollOptionId: optionId });
      setLocalPoll(updated);

      trackEvent(
        click({
          clickTarget: ClickTarget.PollVote,
          clickText: String(optionId),
          contentId: Number(pollId),
        })
      );
    } catch {
      toast.error('Unable to cast your vote, please try again.');
    }
  };

  const handleViewChange = (inView: boolean) => {
    if (inView && poll) {
      trackEvent(
        impression({
          impressionElement: ImpressionElement.ArticlePoll,
          context: pollId,
        })
      );
    }
  };

  if (isLoading) {
    return (
      <div className="my-32 flex items-center justify-center py-16">
        <CustomSpinner size={10} color={CustomSpinnerColor.PrimaryMagenta} />
      </div>
    );
  }

  if (!poll) return null;

  const isClosed = isClosedPoll(poll);
  const isSubscriber = !!currentUser?.is_subscriber;
  const canVote = isSubscriber && !isClosed;
  const showPollResult = isSubscriber && (poll.user_voted || (poll.total_votes === 0 && canVote));

  return (
    <Track>
      <InView className="my-32" threshold={0.5} triggerOnce onChange={handleViewChange}>
        <PollCard poll={poll} onVote={handleVote} />
        {showPollResult && (
          <div className="mt-8 px-16">
            <PollResult
              totalVotes={poll.total_votes}
              expiresAt={poll.expires_at}
              pollClosed={isClosed}
              canVote={canVote}
              userVoted={poll.user_voted}
            />
          </div>
        )}
      </InView>
    </Track>
  );
}
